import React, { useState, useEffect, useRef } from "react";
import { User } from "../../../redux/reducers/UserSlice/UserSliceType";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { deleteMessage } from "../../../redux/reducers/UserSlice/UserSlice";
import { Message } from "redux/reducers/ChatReducer/ChatSliceTypes";
import { getMessageTime } from "../Chat";
import NcImage from "shared/NcImage/NcImage";
import {
  FiTrash2,
  FiDownload,
  FiFileText,
  FiMoreVertical,
  FiCheck,
  FiCheckCircle,
} from "react-icons/fi";
import moment from "moment";

interface MessageItemType {
  message: any;
  currentUser: User | null;
  selectChat: any;
  getName: (name: string) => string;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

const MessageItem = ({
  message,
  currentUser,
  selectChat,
  getName,
  setMessages,
}: MessageItemType) => {
  const [menuVisible, setMenuVisible] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const dispatch = useDispatch<AppDispatch>();

  const myId = currentUser?.id;
  const isHost = currentUser?.is_host;
  const isStudent = currentUser?.is_student;

  const toggleMenu = (messageId: string) => {
    setMenuVisible((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handleDelete = async (messageId: string) => {
    try {
      const numericMessageId = Number(messageId);

      const action = await dispatch(
        deleteMessage({ chatId: selectChat?.id, messageId: numericMessageId })
      );
      toggleMenu(messageId);

      if (action?.payload?.success) {
        setMessages((prevMessages) => {
          const updatedMessages = prevMessages?.map((group: any) => {
            const filteredMessages = group.messages.filter(
              (message: any) => message.id !== numericMessageId
            );
            return { ...group, messages: filteredMessages };
          });
          return updatedMessages.filter(
            (group: any) => group.messages.length > 0
          );
        });
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const shouldShowMessage = (item: any) => {
    const isCurrentUser = myId === item.sender_id;
    return (
      item.event_for === "both" ||
      (isHost && item.event_for === "host") ||
      (isStudent && item.event_for === "student") ||
      isCurrentUser
    );
  };

  const getMessageAlignment = (item: any) => {
    const isCurrentUser = myId === item.sender_id;

    if (item.event_for === "both") {
      return isCurrentUser ? "justify-end" : "justify-start";
    }

    if (item.event_for === "host" || item.event_for === "student") {
      return "justify-center";
    }

    return isCurrentUser ? "justify-end" : "justify-start";
  };

  const isRoleSpecificMessage = (eventFor: string) => {
    return eventFor === "host" || eventFor === "student";
  };

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
    setImageModalOpen(true);
  };

  const getFileIcon = (type: string) => {
    if (type.includes("pdf")) return "📄";
    if (type.includes("word") || type.includes("doc")) return "📝";
    if (type.includes("excel") || type.includes("sheet")) return "📊";
    if (type.includes("video")) return "🎥";
    return "📎";
  };

  const getFileName = (url: string) => {
    const parts = url.split("/");
    return parts[parts.length - 1] || "File";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(menuVisible).forEach((key) => {
        if (
          menuVisible[key] &&
          menuRefs.current[key] &&
          !menuRefs.current[key]?.contains(event.target as Node)
        ) {
          setMenuVisible((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      });
    };

    if (Object.values(menuVisible).some((visible) => visible)) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuVisible]);

  return (
    <div className="space-y-6">
      {/* Date Divider */}
      <div className="flex items-center justify-center my-6">
        <div className="px-4 py-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full">
          <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
            {message.date}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-3">
        {message.messages?.map((item: any, index: number) => {
          if (!shouldShowMessage(item)) {
            return null;
          }

          const isCurrentUser = myId === item.sender_id;
          const isRoleMessage = isRoleSpecificMessage(item.event_for);
          const messageAlignment = getMessageAlignment(item);
          const messageTime = getMessageTime(item?.created_at);
          const hasAttachments = item?.attachments?.length > 0;

          // System/Role Message (centered) - FIXED
          if (isRoleMessage) {
            const isCancellation = item.event_message === "booking canceled";
            const isRequest = item.event_message === "booking request";
            const isConfirmation = item.event_message === "booking confirmed";

            let title = "System Message";
            let messageText = item.body?.name || "";
            let colorScheme = "default";

            if (isCancellation) {
              const isMyCancellation = messageText.toLowerCase().includes("you cancelled");
              if (isMyCancellation) {
                title = "You Cancelled This Booking";
              } else {
                if (messageText.toLowerCase().includes("host")) {
                  title = "Host Cancelled Booking";
                } else if (messageText.toLowerCase().includes("guest")) {
                  title = "Guest Cancelled Booking";
                } else {
                  title = "Booking Cancelled";
                }
              }
              colorScheme = "cancel";
            } else if (isRequest) {
              title = item.event_for === "host" ? "New Booking Request" : "Booking Request Sent";
              colorScheme = "request";
            } else if (isConfirmation) {
              title = "Booking Confirmed";
              colorScheme = "confirm";
            }

            return (
              <div key={item.id} className="flex justify-center my-4">
                <div
                  className={`max-w-md px-4 py-3 rounded-xl border-2 shadow-md ${
                    colorScheme === "cancel"
                      ? "bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border-red-300 dark:border-red-700"
                      : colorScheme === "request"
                      ? "bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-300 dark:border-blue-700"
                      : colorScheme === "confirm"
                      ? "bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border-green-300 dark:border-green-700"
                      : "bg-gradient-to-br from-[#F56948]/10 via-[#F56948]/5 to-[#FF8A6E]/10 dark:from-[#F56948]/20 dark:via-[#F56948]/15 dark:to-[#FF8A6E]/20 border-[#F56948]/30 dark:border-[#F56948]/50"
                  }`}
                >
                  {/* Icon and Title */}
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {colorScheme === "cancel" && (
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    
                    {colorScheme === "request" && (
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                    
                    {colorScheme === "confirm" && (
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    
                    {/* DEFAULT ICON with Brand Color */}
                    {colorScheme === "default" && (
                      <svg className="w-5 h-5 text-[#F56948]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    )}
                    
                    <p className={`text-sm font-bold uppercase tracking-wide ${
                      colorScheme === "cancel"
                        ? "text-red-700 dark:text-red-300"
                        : colorScheme === "request"
                        ? "text-blue-700 dark:text-blue-300"
                        : colorScheme === "confirm"
                        ? "text-green-700 dark:text-green-300"
                        : "text-[#F56948] dark:text-[#FF8A6E]"
                    }`}>
                      {title}
                    </p>
                  </div>

                  {/* Message Text */}
                  <p className={`text-sm text-center leading-relaxed ${
                    colorScheme === "cancel"
                      ? "text-red-900 dark:text-red-100"
                      : colorScheme === "request"
                      ? "text-blue-900 dark:text-blue-100"
                      : colorScheme === "confirm"
                      ? "text-green-900 dark:text-green-100"
                      : "text-[#F56948] dark:text-[#FF8A6E]"
                  }`}>
                    {messageText}
                  </p>
                </div>
              </div>
            );
          }

          // Regular Message Bubbles
          return (
            <div key={item.id} className={`flex ${messageAlignment} group`}>
              {/* Avatar for received messages */}
              {!isCurrentUser && (
                <div className="flex-shrink-0 mr-2">
                  {item.sender?.image ? (
                    <img
                      src={item.sender.image}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-white dark:ring-neutral-800"
                      onError={(e) => {
                        e.currentTarget.src =
                          "https://www.ihna.edu.au/blog/wp-content/uploads/2022/10/user-dummy.png";
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold ring-2 ring-white dark:ring-neutral-800">
                      {item.sender?.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  )}
                </div>
              )}

              {/* Message Content */}
              <div
                className={`flex flex-col max-w-[75%] sm:max-w-[65%] ${
                  isCurrentUser ? "items-end" : "items-start"
                }`}
              >
                {/* Sender Name (for received messages) */}
                {!isCurrentUser && (
                  <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1 ml-2">
                    {item.sender?.name || "Unknown"}
                  </span>
                )}

                {/* Message Bubble */}
                <div className="relative">
                  <div
                    className={`rounded-2xl shadow-sm transition-all ${
                      isCurrentUser
                        ? "bg-[#F56948] text-white rounded-br-md"
                        : "bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white border border-neutral-200 dark:border-neutral-700 rounded-bl-md"
                    }`}
                  >
                    {/* Attachments */}
                    {hasAttachments && (
                      <div className="p-2">
                        <div className="grid grid-cols-2 gap-2">
                          {item.attachments.map(
                            (attachment: any, idx: number) => {
                              const isImage =
                                attachment.type.startsWith("image/");
                              const isVideo =
                                attachment.type.startsWith("video/");

                              if (isImage) {
                                return (
                                  <button
                                    key={idx}
                                    onClick={() =>
                                      handleImageClick(attachment.url)
                                    }
                                    className="relative rounded-xl overflow-hidden group/image hover:opacity-90 transition-opacity"
                                  >
                                    <img
                                      src={attachment.url}
                                      alt="attachment"
                                      className="w-full h-32 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/20 transition-colors flex items-center justify-center">
                                      <svg
                                        className="w-8 h-8 text-white opacity-0 group-hover/image:opacity-100 transition-opacity"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                        />
                                      </svg>
                                    </div>
                                  </button>
                                );
                              }

                              if (isVideo) {
                                return (
                                  <div
                                    key={idx}
                                    className="relative rounded-xl overflow-hidden"
                                  >
                                    <video
                                      src={attachment.url}
                                      controls
                                      className="w-full h-32 object-cover bg-black"
                                    />
                                  </div>
                                );
                              }

                              // File attachments
                              return (
                                <a
                                  key={idx}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                                    isCurrentUser
                                      ? "bg-white/10 border-white/20 hover:bg-white/20"
                                      : "bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-600 hover:border-neutral-300 dark:hover:border-neutral-500"
                                  }`}
                                >
                                  <div
                                    className={`text-2xl flex-shrink-0 ${
                                      isCurrentUser ? "opacity-90" : ""
                                    }`}
                                  >
                                    {getFileIcon(attachment.type)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className={`text-xs font-medium truncate ${
                                        isCurrentUser
                                          ? "text-white"
                                          : "text-neutral-900 dark:text-white"
                                      }`}
                                    >
                                      {getFileName(attachment.url)}
                                    </p>
                                    <p
                                      className={`text-xs ${
                                        isCurrentUser
                                          ? "text-white/70"
                                          : "text-neutral-500 dark:text-neutral-400"
                                      }`}
                                    >
                                      Document
                                    </p>
                                  </div>
                                  <FiDownload
                                    className={`flex-shrink-0 ${
                                      isCurrentUser
                                        ? "text-white"
                                        : "text-neutral-400"
                                    }`}
                                    size={16}
                                  />
                                </a>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}

                    {/* Message Text */}
                    {item.body?.name && (
                      <div
                        className={`px-4 ${
                          hasAttachments ? "pb-3 pt-1" : "py-3"
                        }`}
                      >
                        {item.body?.url ? (
                          <a
                            href={item.body.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm underline hover:no-underline ${
                              isCurrentUser
                                ? "text-white"
                                : "text-blue-600 dark:text-blue-400"
                            }`}
                          >
                            {item.body.name}
                          </a>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {item.body.name}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Message Footer (Time + Status) */}
                    <div
                      className={`flex items-center gap-1 px-4 pb-2 ${
                        !item.body?.name && !hasAttachments ? "pt-2" : ""
                      }`}
                    >
                      <span
                        className={`text-xs ${
                          isCurrentUser
                            ? "text-white/70"
                            : "text-neutral-500 dark:text-neutral-400"
                        }`}
                      >
                        {messageTime}
                      </span>
                      {isCurrentUser && (
                        <FiCheckCircle
                          size={12}
                          className="text-white/70 ml-1"
                        />
                      )}
                    </div>
                  </div>

                  {/* Delete Menu for sent messages */}
                  {isCurrentUser && (
                    <div className="absolute top-1 -left-8 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => toggleMenu(item.id)}
                        className="p-1.5 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-all"
                      >
                        <FiMoreVertical size={16} />
                      </button>

                      {menuVisible[item.id] && (
                        <div
                          ref={(ref) => (menuRefs.current[item.id] = ref)}
                          className="absolute top-0 left-0 mt-8 bg-white dark:bg-neutral-800 shadow-xl rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden z-50 min-w-[140px]"
                        >
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <FiTrash2 size={14} />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Modal */}
      {imageModalOpen && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setImageModalOpen(false)}
        >
          <button
            onClick={() => setImageModalOpen(false)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <img
            src={selectedImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default MessageItem;
